import { useGetExternalOrganizations } from 'components/IncidentConfig/hooks/useGetExternalOrganizations';
import { FormProvider, useForm } from 'react-hook-form';
import FormTextField from '../IAP/formFields/FormTextField';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useAssignExternalUser } from 'components/IncidentConfig/hooks/useAssignExternalUser';
import Loader from 'components/Loader/Loader';
import { useIsEmailInGroup } from 'components/IncidentConfig/hooks/useIsEmailInGroup';
import FormCreatableSelectField from 'components/IAP/formFields/FormCreatableSelectField';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { validEmailPattern } from 'utils/validateEmailAddress';
import { validPhoneNumberPattern } from 'utils/validatePhoneNumberRegex';

type ExternalMemberSelectFormData = {
  externalUserEmail: string;
  externalUserPhoneNumber: string;
  externalUserFirstName: string;
  externalUserLastName: string;
  externalUserOrganization: {
    label: string;
    value: string;
  };
};

type AssignExternalUserFormProps = {
  incidentId: string;
  positionId: string;
  positionName: string;
  selectingLeader?: boolean;
  closeTable: () => void;
};

const AssignNewExternalUserForm: React.FC<AssignExternalUserFormProps> = ({
  incidentId,
  positionId,
  positionName,
  selectingLeader = true,
  closeTable,
}) => {
  const {
    data: externalOrganizations,
    isLoading,
    isError,
  } = useGetExternalOrganizations();
  const assignExternalUserMutation = useAssignExternalUser();
  const isEmailInGroup = useIsEmailInGroup();
  const groupId = useGroupGuid();

  const form = useForm<ExternalMemberSelectFormData>({
    mode: 'onChange',
    defaultValues: {
      externalUserEmail: '',
      externalUserPhoneNumber: '',
      externalUserFirstName: '',
      externalUserLastName: '',
      externalUserOrganization: {
        label: '',
        value: '',
      },
    },
  });

  const { handleSubmit } = form;

  const handleAssignExternalUser = (data: ExternalMemberSelectFormData) => {
    assignExternalUserMutation.mutate({
      groupId,
      incidentId,
      positionId,
      positionName,
      isLeader: selectingLeader,
      externalUser: {
        emailAddress: data.externalUserEmail,
        firstName: data.externalUserFirstName,
        lastName: data.externalUserLastName,
        phoneNumber: data.externalUserPhoneNumber,
        organization: {
          id: data.externalUserOrganization.value,
          name: data.externalUserOrganization.label,
        },
      },
    });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(handleAssignExternalUser)(e);
    closeTable();
  };

  return (
    <div className=" border rounded pt-1 ps-3 pe-3 pb-1 border-primary mb-4">
      {isError && (
        <div className="d-flex align-items-center justify-content-center text-danger">
          Error loading external organizations
        </div>
      )}
      {isLoading && (
        <div className="d-flex align-items-center justify-content-center">
          <Loader showLoading={true} fullscreen={false} />
        </div>
      )}
      {!isError && !isLoading && (
        <FormProvider {...form}>
          <form
            onSubmit={handleFormSubmit}
            className="d-flex flex-column justify-content-between flex-grow-1"
          >
            <div className="p-4">
              <div className="row">
                <FormTextField
                  title="Email"
                  fieldName="externalUserEmail"
                  rules={{
                    required: { value: true, message: 'Email is required' },
                    pattern: {
                      value: validEmailPattern,
                      message: 'Invalid email address',
                    },
                    validate: {
                      notInGroup: async (value) => {
                        if (!value) return true; // Skip if email is empty, required rule will catch it
                        if (!value.match(validEmailPattern)) return true; // Skip if email format is invalid, pattern rule will catch it
                        const response = await isEmailInGroup.mutateAsync({
                          emailAddress: value,
                        });

                        return (
                          !response.isInGroup ||
                          'Email is already associated with a PRATUS user.'
                        );
                      },
                    },
                  }}
                  variant="full"
                />
              </div>
              <div className="row">
                <div className="col">
                  <FormTextField
                    title="First Name"
                    fieldName="externalUserFirstName"
                    rules={{
                      required: {
                        value: true,
                        message: 'First name is required',
                      },
                    }}
                    variant="full"
                  />
                </div>
                <div className="col">
                  <FormTextField
                    title="Last Name"
                    fieldName="externalUserLastName"
                    rules={{
                      required: {
                        value: true,
                        message: 'Last name is required',
                      },
                    }}
                    variant="full"
                  />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col">
                  <FormTextField
                    title="Phone Number"
                    fieldName="externalUserPhoneNumber"
                    rules={{
                      pattern: {
                        value: validPhoneNumberPattern,
                        message: 'Invalid phone number (must be 10 digits)',
                      },
                    }}
                    variant="full"
                  />
                </div>
                <div className="col">
                  <FormCreatableSelectField
                    title="Organization"
                    fieldName="externalUserOrganization"
                    defaultOptions={
                      externalOrganizations?.externalOrganizations.map(
                        (org) => ({
                          label: org.organizationName,
                          value: org.id,
                        })
                      ) || []
                    }
                    rules={{
                      required: {
                        value: true,
                        message: 'Organization is required',
                      },
                      validate: {
                        isSelected: (value) => {
                          return (
                            (value && value.value !== '') ||
                            'Organization is required'
                          );
                        },
                      },
                    }}
                    variant="full"
                  />
                </div>
                <StylishNewButton
                  className="button--primary"
                  type="submit"
                  disabled={
                    assignExternalUserMutation.isPending ||
                    !form.formState.isValid
                  }
                >
                  {assignExternalUserMutation.isPending
                    ? 'Assigning...'
                    : 'Invite and Assign User'}
                </StylishNewButton>
              </div>
            </div>
          </form>
        </FormProvider>
      )}
    </div>
  );
};

export default AssignNewExternalUserForm;
